import { getFormattedAmount } from '@bbkPricingPage/lib/currencies';
import {
  PeriodUnit,
  PricingModel,
  type Discount,
  type Frequency,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

export const getTotalAmount = (
  price: Price,
  quantity: number,
  key: 'amount' | 'strikethrough_amount'
): number => {
  // If strikethrough_amount is not provided, then return amount
  if (price.pricing_model === PricingModel.flat_fee) {
    return price[key] ?? price.amount ?? 0;
  } else if (price.pricing_model === PricingModel.per_unit) {
    return (price[key] ?? price.amount ?? 0) * quantity;
  } else if (price.pricing_model === PricingModel.tiered) {
    return getTieredTotal(price, quantity, key);
  } else if (price.pricing_model === PricingModel.volume) {
    const selectedTier = getMatchingTier(price, quantity);
    return (selectedTier?.[key] ?? selectedTier?.amount ?? 0) * quantity;
  } else if (price.pricing_model === PricingModel.stairstep) {
    const selectedTier = getMatchingTier(price, quantity);
    return selectedTier?.[key] ?? selectedTier?.amount ?? 0;
  } else {
    return price[key] ?? price.amount ?? 0;
  }
};

export const getMatchingTier = (price: Price, quantity: number) => {
  return price.tiers?.find((tier) => {
    if (quantity >= tier.starting_unit) {
      if (tier.ending_unit) {
        if (quantity <= tier.ending_unit) {
          return true;
        }
      } else {
        return true;
      }
    }
    return false;
  });
};

const getTieredTotal = (
  price: Price,
  quantity: number,
  key: 'amount' | 'strikethrough_amount'
) => {
  let total = 0;

  const isMultiDecimalPrice = price.tiers?.some(
    (tier) => tier.starting_unit === 0
  );

  price.tiers?.forEach((tier) => {
    if (quantity >= tier.starting_unit) {
      const qtyInTier = getQuantityInTier(tier, quantity, isMultiDecimalPrice);
      total += (tier[key] ?? tier.amount) * qtyInTier;
    }
  });
  return total;
};

export const getQuantityInTier = (
  tier: { starting_unit: number; ending_unit?: number },
  quantity: number,
  isMultiDecimalPrice?: boolean
): number => {
  let qtyInTier = quantity - tier.starting_unit;
  if (tier.ending_unit) {
    qtyInTier = Math.min(qtyInTier, tier.ending_unit - tier.starting_unit);
  }
  if (!isMultiDecimalPrice) {
    qtyInTier += 1; // Adjust for inclusive counting for non-multi-decimal prices
  }
  return qtyInTier;
};

export const getMonthlyAmountBasedOnApplicability = (
  amount: number,
  period: number,
  periodUnit: PeriodUnit,
  showMonthlyPrice?: boolean
) => {
  return showMonthlyPrice
    ? getMonthlyPrice(amount, period, periodUnit)
    : amount;
};

const getMonthlyPrice = (
  amount: number,
  period: number,
  periodUnit: PeriodUnit
) => {
  if (isEligibleForMonthlyPrice(period, periodUnit)) {
    if (periodUnit === 'month') {
      return amount / period;
    } else if (periodUnit === 'year') {
      return amount / (period * 12);
    }
  }
  return amount;
};

const isEligibleForMonthlyPrice = (period: number, periodUnit: string) => {
  return (period !== 1 && periodUnit === 'month') || periodUnit === 'year';
};

const normalizePeriod = (
  value: number,
  unit: string
): { value: number; unit: string } => {
  if (unit === PeriodUnit.year) {
    return { value: value * 12, unit: PeriodUnit.month };
  } else if (unit === PeriodUnit.week) {
    return { value: value * 7, unit: PeriodUnit.day };
  }
  return { value, unit };
};

export const priceMultiple = (
  planPeriodValue: number,
  planPeriodUnit: PeriodUnit,
  addonPeriodValue?: number,
  addonPeriodUnit?: PeriodUnit
): number => {
  if (!addonPeriodValue || !addonPeriodUnit) return 0;
  const normalizedPlan = normalizePeriod(planPeriodValue, planPeriodUnit);
  const normalizedAddon = normalizePeriod(addonPeriodValue, addonPeriodUnit);

  if (
    normalizedPlan.unit === normalizedAddon.unit &&
    normalizedPlan.value % normalizedAddon.value === 0
  ) {
    return normalizedPlan.value / normalizedAddon.value;
  }
  return 0;
};

// round to two decimal places if required
// 10 => 10
// 10.011 => 10.01
// 10.019 => 10.02
// 10.015 => 10.01
const roundToTwoDecimalIfRequired = (amount: number) => {
  return Math.round(amount * 100) / 100;
};

export const getAmountAfterDiscount = (
  amount: number,
  discounts?: Discount[]
) => {
  let finalAmount = amount;
  if (discounts && discounts.length > 0) {
    // Chargebee apply fixed amount discounts first, then percentage discounts
    // It applies discount in cascading manner
    // discount1 : (base_amount - amount1)
    // discount2 : (discount1 - amount2)
    // final_amount = base_amount - amount1 - amount2
    discounts.forEach((discount) => {
      if (discount.amount != null) {
        finalAmount -= discount.amount;
      }
    });

    // Apply highest percentage discount first
    discounts
      .toSorted((a, b) => {
        return (b.percentage ?? 0) - (a.percentage ?? 0);
      })
      .forEach((discount) => {
        if (discount.percentage != null) {
          const discountAmount = roundToTwoDecimalIfRequired(
            finalAmount * (discount.percentage / 100)
          );
          finalAmount -= discountAmount;
          finalAmount = roundToTwoDecimalIfRequired(finalAmount);
        }
      });
  }
  return Math.max(0, finalAmount); // Prevent negative amounts
};

export const getEffectiveAmount = (
  price: Price,
  quantity: number | undefined | null,
  frequency: Frequency,
  applicableDiscounts?: Discount[],
  skipMonthly: boolean = false
) => {
  quantity = quantity ?? 1;
  const totalAmount = getTotalAmount(price, quantity, 'amount');

  const totalDiscountedAmount = getAmountAfterDiscount(
    totalAmount,
    applicableDiscounts
  );

  const amount = skipMonthly
    ? totalDiscountedAmount
    : getMonthlyAmountBasedOnApplicability(
        totalDiscountedAmount, // if discount === 0, then totalDiscountedAmount === totalAmount
        frequency.period,
        frequency.period_unit,
        frequency.show_monthly_price
      );

  return amount;
};

export const getTotalEffectiveAmountWithoutMonthly = (
  price: Price,
  quantity: number | undefined | null,
  applicableDiscounts?: Discount[]
) => {
  return getEffectiveAmount(
    price,
    quantity,
    {} as Frequency,
    applicableDiscounts,
    true
  );
};

export const formatPriceDisplay = (
  amount: number,
  currency: string,
  frequency: Frequency,
  multiplier?: number
): string | null => {
  const effectiveAmount = multiplier ? amount * multiplier : amount;
  if (frequency.show_monthly_price) {
    const monthlyAmount = getMonthlyAmountBasedOnApplicability(
      effectiveAmount,
      frequency.period,
      frequency.period_unit,
      frequency.show_monthly_price
    );
    return getFormattedAmount(monthlyAmount, currency);
  }
  return getFormattedAmount(effectiveAmount, currency);
};
